import { useState } from "react";
import axios from "axios";
import { MapPin, Heart, Megaphone, Sprout, Send, Loader2 } from "lucide-react";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const NEIGHBORHOODS = [
  "Ballentine Place", "Barraud Park", "Braywood Manor", "Broad Creek",
  "Bruce's Park", "COGIC Highrise", "Coronado", "Inglenook",
  "Cottage Bridge", "Cottage Heights", "Greenwood", "Elmhurst",
  "Norview Heights", "Lindenwood", "Middle Towne Arch", "Norview",
  "Spartan Village", "Stonebridge", "Tanners Creek", "Villa Heights",
  "Wellington Oaks", "Greenhill Farms", "Meadowbrook Woods", "Oakmont North",
  "Oakwood", "Sewells Garden", "St. Andrews Place", "Norvella Heights",
  "Sherwood Forest", "Chesapeake Gardens", "Mamie Homes", "Franklin Arms",
  "Partrea", "Brandon Place", "East Fairmount", "Fairmount Park",
].sort((a, b) => a.localeCompare(b));

const COMMITMENTS = [
  {
    icon: Megaphone,
    title: "Listening First",
    body:
      "We believe solutions should come from the people closest to the challenges.",
  },
  {
    icon: MapPin,
    title: "Neighborhood-Focused Action",
    body:
      "Each community in Ward 3 deserves attention, investment, safety, resources, and respect.",
  },
  {
    icon: Sprout,
    title: "Together We Grow",
    body:
      "From Ballentine to Norview, Broad Creek to Tanners Creek, we are building a people-powered campaign rooted in unity.",
  },
];

export const Ward3Communities = () => {
  const [form, setForm] = useState({ name: "", neighborhood: "", email: "", concern: "" });
  const [loading, setLoading] = useState(false);

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.neighborhood || !form.concern) {
      toast.error("Please fill in all fields");
      return;
    }
    setLoading(true);
    try {
      await axios.post(`${API}/community-concerns`, form);
      toast.success("Thank you. Si has received your concern.");
      setForm({ name: "", neighborhood: "", email: "", concern: "" });
    } catch (err) {
      toast.error(
        err?.response?.data?.detail?.[0]?.msg ||
          "Something went wrong. Please try again."
      );
    } finally {
      setLoading(false);
    }
  };

  return (
    <section
      id="ward-3"
      data-testid="ward3-section"
      className="relative bg-royal-deep py-24 lg:py-32 overflow-hidden"
    >
      {/* Ambient glow */}
      <div className="pointer-events-none absolute inset-0">
        <div className="absolute top-20 -left-40 w-[500px] h-[500px] rounded-full bg-lavender/10 blur-[120px]"></div>
        <div className="absolute bottom-20 -right-40 w-[500px] h-[500px] rounded-full bg-[#5B8DEF]/10 blur-[120px]"></div>
      </div>

      <div className="relative max-w-7xl mx-auto px-5 lg:px-10">
        {/* Hero */}
        <div className="mb-14 lg:mb-20 max-w-3xl">
          <p className="text-[11px] tracking-[0.32em] uppercase text-lavender mb-4">
            04 — Ward 3 & Communities
          </p>
          <h2
            data-testid="ward3-heading"
            className="font-serif-display text-classy text-5xl sm:text-6xl lg:text-7xl leading-[0.95] font-medium"
          >
            Ward 3 <span className="italic font-light">&</span> Communities
          </h2>
          <p className="font-serif-display italic text-lavender text-xl sm:text-2xl lg:text-3xl mt-5">
            Every neighborhood matters. Every voice counts. Together, we grow.
          </p>
          <p className="text-classy-soft/85 text-[15px] lg:text-base leading-relaxed mt-7 max-w-2xl">
            Ward 3 is made up of proud, historic, and growing Norfolk
            communities. This campaign is committed to listening to every
            neighborhood, uplifting every family, and building solutions that
            reflect the real needs of the people who live here.
          </p>
        </div>

        {/* Map */}
        <div className="mb-20 lg:mb-28">
          <div className="relative max-w-3xl mx-auto">
            {/* Lavender frame */}
            <div className="absolute -inset-2 lg:-inset-3 bg-gradient-to-br from-lavender/40 via-lavender/10 to-[#5B8DEF]/30 rounded-sm blur-sm"></div>
            <div className="relative bg-royal border border-lavender/30 rounded-sm p-2 lg:p-3 shadow-[0_30px_80px_-20px_rgba(0,0,0,0.7)]">
              <img
                src="/assets/ward3-map.jpg"
                alt="Ward 3 Communities — Together We Grow · Community Is Our Power"
                data-testid="ward3-map"
                className="w-full h-auto rounded-sm"
              />
            </div>
          </div>
          <p className="text-center text-[11px] tracking-[0.32em] uppercase text-classy-soft mt-6">
            Ward 3 Communities · Community Is Our Power
          </p>
        </div>

        {/* Our Commitment to Ward 3 */}
        <div className="mt-16 lg:mt-24 mb-20 lg:mb-28">
          <div className="text-center mb-14">
            <p className="text-[11px] tracking-[0.32em] uppercase text-lavender mb-3">
              Our Commitment to Ward 3
            </p>
          </div>

          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4 lg:gap-6">
            {COMMITMENTS.map((c, idx) => (
              <article
                key={c.title}
                data-testid={`ward3-commitment-${idx}`}
                className="group relative bg-[#1B0640]/60 backdrop-blur border border-white/10 hover:border-lavender/40 p-8 lg:p-10 transition-all duration-500 overflow-hidden"
              >
                <div className="absolute -top-12 -right-12 w-32 h-32 rounded-full bg-lavender/5 group-hover:bg-lavender/15 blur-2xl transition-colors duration-700"></div>
                <div className="relative">
                  <div className="inline-flex items-center justify-center h-14 w-14 rounded-full bg-lavender/10 border border-lavender/30 mb-7">
                    <c.icon size={22} className="text-lavender" strokeWidth={1.5} />
                  </div>
                  <p className="font-serif-display text-classy/40 text-xl italic mb-2">
                    0{idx + 1}
                  </p>
                  <h4 className="font-serif-display text-classy text-2xl lg:text-3xl mb-4 leading-tight">
                    {c.title}
                  </h4>
                  <p className="text-classy-soft/85 leading-relaxed text-[14px]">
                    &ldquo;{c.body}&rdquo;
                  </p>
                </div>
              </article>
            ))}
          </div>
        </div>

        {/* Tell Us What Your Community Needs */}
        <div className="border border-lavender/20 bg-gradient-to-b from-[#1B0640]/80 to-[#0E0526]/80 backdrop-blur-xl rounded-sm p-8 sm:p-12 lg:p-16">
          <div className="grid lg:grid-cols-12 gap-10 items-start">
            <div className="lg:col-span-5">
              <div className="inline-flex items-center justify-center h-16 w-16 rounded-full bg-lavender/10 border border-lavender/30 mb-6">
                <Heart className="text-lavender" size={26} strokeWidth={1.5} />
              </div>
              <p className="text-[11px] tracking-[0.32em] uppercase text-lavender mb-4">
                Your voice. Si&rsquo;s table.
              </p>
              <h3
                data-testid="ward3-form-heading"
                className="font-serif-display text-classy text-4xl sm:text-5xl lg:text-6xl leading-[0.95] font-medium mb-5"
              >
                Tell Us <span className="italic font-light">What</span> Your Community Needs
              </h3>
              <p className="text-classy-soft/85 text-[15px] leading-relaxed">
                Si reads every submission. Share one issue you want addressed
                &mdash; housing, education, safety, healthcare, transit, parks, or
                anything else on your block.
              </p>
            </div>

            <form
              onSubmit={onSubmit}
              data-testid="ward3-form"
              className="lg:col-span-7 space-y-5"
            >
              <div className="grid sm:grid-cols-2 gap-4">
                <input
                  type="text"
                  placeholder="Name"
                  value={form.name}
                  onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
                  data-testid="ward3-form-name"
                  required
                  className="w-full bg-transparent border border-white/25 focus:border-lavender outline-none text-classy placeholder-classy-soft/50 px-5 py-4 text-sm rounded-sm transition-colors"
                />
                <input
                  list="ward3-neighborhoods"
                  type="text"
                  placeholder="Neighborhood"
                  value={form.neighborhood}
                  onChange={(e) =>
                    setForm((f) => ({ ...f, neighborhood: e.target.value }))
                  }
                  data-testid="ward3-form-neighborhood"
                  required
                  className="w-full bg-transparent border border-white/25 focus:border-lavender outline-none text-classy placeholder-classy-soft/50 px-5 py-4 text-sm rounded-sm transition-colors"
                />
                <datalist id="ward3-neighborhoods">
                  {NEIGHBORHOODS.map((n) => (
                    <option key={n} value={n} />
                  ))}
                </datalist>
              </div>
              <input
                type="email"
                placeholder="Email address"
                value={form.email}
                onChange={(e) => setForm((f) => ({ ...f, email: e.target.value }))}
                data-testid="ward3-form-email"
                required
                className="w-full bg-transparent border border-white/25 focus:border-lavender outline-none text-classy placeholder-classy-soft/50 px-5 py-4 text-sm rounded-sm transition-colors"
              />
              <textarea
                rows={5}
                placeholder="What is one issue you want addressed in your community?"
                value={form.concern}
                onChange={(e) => setForm((f) => ({ ...f, concern: e.target.value }))}
                data-testid="ward3-form-concern"
                required
                className="w-full bg-transparent border border-white/25 focus:border-lavender outline-none text-classy placeholder-classy-soft/50 px-5 py-4 text-sm rounded-sm transition-colors resize-none"
              />
              <button
                type="submit"
                disabled={loading}
                data-testid="ward3-form-submit"
                className="btn-classy w-full sm:w-auto px-10 py-4 rounded-full text-xs uppercase inline-flex items-center justify-center gap-2 disabled:opacity-60"
              >
                {loading ? (
                  <>
                    <Loader2 size={16} className="animate-spin" /> Sending
                  </>
                ) : (
                  <>
                    <Send size={14} /> Share My Community Concern
                  </>
                )}
              </button>
            </form>
          </div>
        </div>
      </div>
    </section>
  );
};

export default Ward3Communities;
